/**
 * Assassin Workshop — Cart Drawer lazy bootstrap
 * Lot 8 / v1.2.0
 * Keeps the complete cart drawer and its base CSS off the initial path until cart intent.
 * Submit interception runs after capture-phase safety/attribution guards.
 */
(() => {
  'use strict';

  if (window.AWCartDrawerBootstrap?.version) return;

  const bootstrapScript = document.currentScript;
  let loading = null;
  let coreLoading = null;
  let styleLoading = null;
  let coreLoaded = Boolean(window.AWCartDrawerCore?.version);
  let styleLoaded = false;
  let loaded = false;

  const assetSrc = (filename) => {
    try {
      const url = new URL(bootstrapScript?.src || '', window.location.href);
      url.pathname = url.pathname.replace(/cart-drawer\.js$/, filename);
      return url.href;
    } catch (_) {
      return '';
    }
  };

  const coreSrc = assetSrc('cart-drawer-core.js');
  const styleSrc = assetSrc('cart-drawer.css');

  const loadStyle = () => {
    if (styleLoaded) return Promise.resolve();
    if (styleLoading) return styleLoading;
    if (!styleSrc) return Promise.reject(new Error('Cart drawer CSS URL unavailable'));

    const existing = Array.from(document.querySelectorAll('link[rel="stylesheet"]')).find((link) => {
      try { return new URL(link.href, window.location.href).pathname.endsWith('/cart-drawer.css'); }
      catch (_) { return false; }
    });
    if (existing?.sheet) {
      styleLoaded = true;
      return Promise.resolve();
    }

    styleLoading = new Promise((resolve, reject) => {
      const node = existing || document.createElement('link');
      const onLoad = () => {
        styleLoaded = true;
        resolve();
      };
      const onError = () => {
        styleLoading = null;
        reject(new Error('Cart drawer CSS failed to load'));
      };
      node.addEventListener('load', onLoad, { once: true });
      node.addEventListener('error', onError, { once: true });
      if (!existing) {
        node.rel = 'stylesheet';
        node.href = styleSrc;
        node.fetchPriority = 'low';
        node.dataset.awCartDrawerStyle = 'true';
        document.head.appendChild(node);
      }
    });
    return styleLoading;
  };

  const loadCore = () => {
    if (coreLoaded || window.AWCartDrawerCore?.version) {
      coreLoaded = true;
      return Promise.resolve(window.AWCartDrawerCore);
    }
    if (coreLoading) return coreLoading;
    if (!coreSrc) return Promise.reject(new Error('Cart drawer core URL unavailable'));

    coreLoading = new Promise((resolve, reject) => {
      const node = document.createElement('script');
      node.src = coreSrc;
      node.async = true;
      node.dataset.awCartDrawerCore = 'true';
      node.addEventListener('load', () => {
        coreLoaded = Boolean(window.AWCartDrawerCore?.version);
        if (coreLoaded) resolve(window.AWCartDrawerCore);
        else reject(new Error('Cart drawer core did not initialize'));
      }, { once: true });
      node.addEventListener('error', () => {
        coreLoading = null;
        reject(new Error('Cart drawer core failed to load'));
      }, { once: true });
      document.head.appendChild(node);
    });
    return coreLoading;
  };

  const load = () => {
    if (loaded && window.AWCartDrawerCore?.version) return Promise.resolve(window.AWCartDrawerCore);
    if (loading) return loading;

    loading = Promise.all([loadStyle(), loadCore()])
      .then(([, api]) => {
        loaded = true;
        return api;
      })
      .catch((error) => {
        loading = null;
        throw error;
      });
    return loading;
  };

  const isCartLink = (target) => {
    const link = target?.closest?.('a[href*="/cart"]');
    if (!link || link.closest('[data-cart-drawer]') || link.hasAttribute('data-no-drawer')) return null;
    return link;
  };

  const isAddForm = (target) => {
    const form = target?.closest?.('form[action*="/cart/add"]');
    if (!form || form.hasAttribute('data-no-ajax-cart')) return null;
    return form;
  };

  const isCartIntent = (target) => Boolean(
    isCartLink(target) ||
    isAddForm(target) ||
    target?.closest?.('[data-add-button],[data-sticky-button],[data-cart-open]')
  );

  const warm = (event) => {
    if (isCartIntent(event.target)) load().catch(() => {});
  };

  document.addEventListener('pointerover', warm, { capture: true, passive: true });
  document.addEventListener('pointerdown', warm, { capture: true, passive: true });
  document.addEventListener('touchstart', warm, { capture: true, passive: true });
  document.addEventListener('focusin', warm, true);

  document.addEventListener('click', (event) => {
    if (loaded || event.defaultPrevented || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const link = isCartLink(event.target);
    if (!link) return;
    event.preventDefault();
    load()
      .then((api) => {
        if (typeof api?.open === 'function') return api.open();
        window.location.href = link.href;
        return null;
      })
      .catch(() => { window.location.href = link.href; });
  }, true);

  document.addEventListener('submit', (event) => {
    if (loaded || event.defaultPrevented) return;
    const form = isAddForm(event.target);
    if (!form) return;
    event.preventDefault();
    load()
      .then((api) => {
        if (typeof api?.addForm === 'function') return api.addForm(form);
        form.submit();
        return false;
      })
      .catch(() => form.submit());
  });

  document.addEventListener('cart:refresh', () => {
    if (loaded) return;
    load().then((api) => api?.open?.()).catch(() => {});
  });

  window.addEventListener('pageshow', (event) => {
    if (event.persisted && !loaded) {
      load().then((api) => api?.refresh?.(false)).catch(() => {});
    }
  });

  document.addEventListener('shopify:section:load', (event) => {
    if (event.target.querySelector?.('[data-cart-drawer]') || event.target.matches?.('[data-cart-drawer]')) {
      load().catch(() => {});
    }
  });

  window.AWCartDrawerBootstrap = {
    version: '1.2.0',
    load,
    get loaded() { return loaded; }
  };

  if (window.Shopify?.designMode) load().catch(() => {});
})();
